import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, Heart, ArrowRight } from 'lucide-react';

import AppLogo from '../components/AppLogo';

const CircleManagement = () => {
  const navigate = useNavigate();
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCircle, setNewCircle] = useState({ name: '', description: '' });

  const fetchCircles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/circles`);
      setCircles(response.data);
    } catch (error) { toast.error('Could not load circles'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCircles(); }, [fetchCircles]);

  const handleCreateCircle = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/circles`, newCircle);
      toast.success('Circle created!');
      setCircles([...circles, response.data]);
      setNewCircle({ name: '', description: '' });
      setCreateDialogOpen(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not create circle'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  return (
    <div className="min-h-screen pb-24" data-testid="circle-management">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo />
            <div>
              <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Circle</h1>
              <p className="text-xs text-muted-foreground">{circles.length} circle{circles.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-9 text-sm" data-testid="create-new-circle-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Circle
              </Button>
            </DialogTrigger>
            <DialogContent className="surface-card border-0 shadow-xl">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create Circle</DialogTitle>
                <DialogDescription>Start building your support network</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCircle} className="space-y-4">
                <div className="space-y-2">
                  <Label>Circle name</Label>
                  <Input data-testid="circle-name-input" placeholder="e.g., Sarah's Care Circle" value={newCircle.name} onChange={(e) => setNewCircle({ ...newCircle, name: e.target.value })} className="rounded-xl" required />
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea data-testid="circle-description-input" placeholder="What's this circle for?" value={newCircle.description} onChange={(e) => setNewCircle({ ...newCircle, description: e.target.value })} className="resize-none rounded-xl" />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="submit-circle-btn">Create Circle</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6">
        {circles.length > 0 ? (
          <div className="space-y-3">
            {circles.map((circle) => (
              <div
                key={circle.id}
                className="surface-card-hover p-5 cursor-pointer group"
                onClick={() => navigate(`/circles/${circle.id}`)}
                data-testid={`circle-card-${circle.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-200 transition-colors">
                    <Users className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-foreground group-hover:text-cyan-600 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {circle.name}
                    </h3>
                    {circle.description && <p className="text-sm text-muted-foreground mt-0.5 truncate">{circle.description}</p>}
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                      <Heart className="w-3 h-3" />
                      <span>Created {new Date(circle.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-cyan-600 transition-colors flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-cyan-100 rounded-3xl flex items-center justify-center mb-5">
              <Users className="w-10 h-10 text-cyan-600" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>No circles yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-xs">Create your first care circle to start building your support network</p>
            <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary" data-testid="create-first-circle-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Your First Circle
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default CircleManagement;
